/**
 * "Are you sure about this one?" — the sheet that opens when you tap a chip in
 * your hand.
 *
 * Playing a chip is irreversible the moment the last fist closes, and the chip
 * you are choosing between is 56 pixels wide with its rules text hidden. Asking
 * someone to commit from that is asking them to guess. This shows the chip at
 * full size with its ability spelled out, and makes locking it in a deliberate
 * second tap.
 *
 * It is a real modal, so it owes the usual debts: focus moves in, Escape and the
 * backdrop close it, Tab cannot wander out behind it, and focus returns to
 * whatever opened it.
 */

import { useCallback, useEffect, useRef } from 'react';
import { CHIPS, type ChipId } from '@shared/chips';
import { Chip } from '../art/Chip';

export interface ChipSheetProps {
  chip: ChipId;
  /** Sits above the chip name — "Play this chip?" and so on. */
  eyebrow: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Extra context under the ability text, e.g. what happens after it resolves. */
  note?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ChipSheet({ chip, eyebrow, confirmLabel, cancelLabel, note, onConfirm, onCancel }: ChipSheetProps) {
  const def = CHIPS[chip];
  const panel = useRef<HTMLDivElement | null>(null);
  const confirmBtn = useRef<HTMLButtonElement | null>(null);
  /** Whatever had focus when this opened — usually the chip that was tapped. */
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null;
    // Focus the confirm button rather than the heading: a screen reader reads the
    // dialog's label on entry either way, and this puts the primary action under
    // the very next keypress.
    confirmBtn.current?.focus();
    return () => {
      // Only take focus back if it is still inside the sheet — if something else
      // has claimed it since, stealing it would be the rude thing to do.
      if (panel.current?.contains(document.activeElement)) opener.current?.focus?.();
    };
  }, []);

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        onCancel();
        return;
      }
      if (ev.key !== 'Tab') return;
      // A minimal focus trap. Without it Tab walks out of the dialog and into the
      // hand behind it, where every chip is still a button.
      const focusable = panel.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (ev.shiftKey && document.activeElement === first) {
        ev.preventDefault();
        last.focus();
      } else if (!ev.shiftKey && document.activeElement === last) {
        ev.preventDefault();
        first.focus();
      }
    },
    [onCancel],
  );

  return (
    <div className="sheet-backdrop" onKeyDown={onKeyDown}>
      {/*
        A button, not a div with a click handler: the backdrop is a real dismiss
        control and pointer-only dismissal is a trap for anyone not using a mouse.
        It is hidden from the accessibility tree because the sheet's own Cancel
        button says the same thing in words.
      */}
      <button type="button" className="sheet-backdrop__hit" tabIndex={-1} aria-hidden="true" onClick={onCancel} />
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        aria-describedby="sheet-text"
        ref={panel}
      >
        <p className="sheet__eyebrow">{eyebrow}</p>
        <div className="sheet__chip">
          <Chip id={chip} size="lg" />
        </div>
        <h2 className="sheet__title" id="sheet-title">
          {def.name}
        </h2>
        <p className="sheet__text" id="sheet-text">
          {def.text}
        </p>
        {note ? <p className="sheet__note">{note}</p> : null}
        <div className="sheet__actions">
          <button type="button" className="btn" ref={confirmBtn} onClick={onConfirm}>
            {confirmLabel}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
